/*!
 * vue-masonry-css v1.0.3
 * https://github.com/paulcollett/vue-masonry-css
 * Released under the MIT License.
 */

export default {
	name: 'LbaCustomMasonry',
	props: {
		tag: {
			type: [String],
			default: 'div',
		},
		cols: {
			type: [Object, Number, String],
			default: 2,
		},
		gutter: {
			type: [Object, Number, String],
			default: 0,
		},
		css: {
			type: [Boolean],
			default: true,
		},
		columnTag: {
			type: [String],
			default: 'div',
		},
		masonryClass: {
			type: [String, Array, Object],
			default: function () { return ['lba-custom-masonry']; },
		},
		columnClass: {
			type: [String, Array, Object],
			default: function () { return ['lba-masonry-column']; },
		},
		columnAttr: {
			type: [Object],
			default: function () { return ({}); },
		},
		columnsSettings: {
			type: Array,
			default: null,
		},
	},
	data() {
		return {
			displayColumns: 2,
			displayGutter: 0,
			masonryKey: this.$generateUID(),
			masonryWidth: null,
			previsousMasonryWidth: null,
			masonryElement: null,
			colMinWidth: null,
			breakpoints: [],
			pinned: null,
			resizeDebouncer: null,
		};
	},
	created() {
		this.resizeDebouncer = new this.$Debouncer(this, this.reCalculate, null, 300);
	},
	mounted() {
		const this$1 = this;

		this.$nextTick(function () {
			this.masonryElement = document.getElementById(`LbaCustomMasonry-${this.masonryKey}`);

			if (this.masonryElement) {
				const widthWatcher = new ResizeObserver(() => {
					this$1.reCalculate();
				});
				widthWatcher.observe(this.masonryElement);
			}

			this$1.reCalculate();
		});

		if (window) {
			window.addEventListener('resize', this.resizeDebouncer);
		}
	},
	updated() {
		if (this._inactive) {
			return;
		}
		const this$1 = this;

		this.$nextTick(function () {
			this$1.reCalculate();
		});
	},
	beforeDestroy() {
		if (window) {
			window.removeEventListener('resize', this.reCalculate);
		}
	},
	methods: {
		reCalculate() {
			if (this._inactive) {
				return;
			}
			if (this.masonryElement) {

				this.masonryWidth = this.masonryElement.offsetWidth;
				this.breakpointValue();
			}
		},
		_getChildItemsInColumnsArray() {
			const this$1 = this;
			const columns = [];
			let childItems = this.$slots.default || [];

			if (childItems.length === 1 &&
				childItems[0].componentOptions &&
				childItems[0].componentOptions.tag === 'transition-group'
			) {
				childItems = childItems[0].componentOptions.children;
			}

			for (let i = 0; i < this$1.displayColumns; i++) {
				columns.push([]);
			}

			childItems = childItems.filter((item) => (item.tag));
			childItems.map((item, index) => {
				const i = index % this$1.displayColumns;
				columns[i].push(item);
			});

			return columns;
		},
		breakpointValue() {
			this.breakpoints = [];
			for (const [key, value] of Object.entries(this.cols)) {
				if (key === 'min') {
					this.colMinWidth = value;
				} else {
					this.breakpoints.push({ width: key, cols: value });
				}
			}

			for (const [key, value] of Object.entries(this.gutter)) {
				const findBreakpoint = this.breakpoints.find((point) => (point.width === key));
				if (findBreakpoint) {
					findBreakpoint.gutter = value;
				} else {
					this.breakpoints.push({
						with: key,
						cols: this.cols.default || this.cols,
						gutter: value,
					});
				}
			}
			this.breakpoints = this.breakpoints.sort((a, b) => {
				if (a === 'default' || b === 'default') return (a === 'default') ? -1 : 1;
				return a - b;
			});

			let brIndex = this.breakpoints.findIndex(
				(point) => ((parseInt(point.width) > this.masonryWidth) || (point.width === 'default'))
			);

			let newColumns = this.breakpoints[brIndex].cols || null;
			let newGutter = this.breakpoints[brIndex].gutter || null;
			while (!newColumns || !newGutter) {
				if (!this.breakpoints[brIndex]) {
					newColumns = newColumns || 2;
					newGutter = newGutter || 20;
				} else {
					newColumns = newColumns || this.breakpoints[brIndex].cols;
					newGutter = newGutter || this.breakpoints[brIndex].gutter;
				}
				brIndex++;
			}

			this.displayColumns = newColumns;
			this.displayGutter = newGutter;
			return { newGutter, newColumns };
		},
	},
	render(createElement) {
		const this$1 = this;

		const columnsContainingChildren = this._getChildItemsInColumnsArray();

		const isGutterSizeUnitless = parseInt(this.displayGutter) === this.displayGutter * 1;
		const gutterSizeWithUnit = isGutterSizeUnitless ? ((this.displayGutter) + 'px') : this.displayGutter;

		const basicColumnStyle = {
			boxSizing: 'border-box',
			backgroundClip: 'padding-box',
			border: '0 solid transparent',
			borderLeftWidth: gutterSizeWithUnit,
		};

		const singleColumnStyle = $_.assign($_.clone(basicColumnStyle), { width: '100%' });
		const equalColumnStyle = $_.assign($_.clone(basicColumnStyle), { width: ((100 / this.displayColumns) + '%') });
		const customColumnStyles = [];
		let countCustomStyles = 0;

		$_.forEach(this.columnsSettings, (w) => {
			countCustomStyles++;
			const width = (w.constructor === Object) ? w : { width: w };
			customColumnStyles.push($_.assign($_.clone(basicColumnStyle), width));
		});

		const columns = columnsContainingChildren.map((children, index) => {
			let columnStyle = null;

			if (this.displayColumns === 1) {
				columnStyle = singleColumnStyle;

			} else if (countCustomStyles > 0) {
				if (customColumnStyles[index - 1]) {
					columnStyle = customColumnStyles[index - 1];
				} else {
					columnStyle = basicColumnStyle;
				}

			} else {
				columnStyle = equalColumnStyle;

			}

			return createElement(
				this$1.columnTag,
				{
					key: index + '-' + columnsContainingChildren.length,
					style: this$1.css ? columnStyle : null,
					class: this$1.columnClass,
					attrs: { ...this$1.columnAttr, id: `LbaMasonryColumn-${index}` },
				},
				children
			);
		});

		const containerStyle = {
			display: ['-webkit-box', '-ms-flexbox', 'flex'],
			marginLeft: ('-' + gutterSizeWithUnit),
		};

		return createElement(
			this.tag,
			{
				attrs: {
					ref: `LbaCustomMasonry-${this.masonryKey}`,
					id: `LbaCustomMasonry-${this.masonryKey}`,
				},
				style: this.css ? containerStyle : null,
				class: this.css ? this.masonryClass : null,
			},
			columns
		);
	},
};
